import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Grid, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab, FormControl, Select, MenuItem, Popover, Button, Divider, CircularProgress, Checkbox, Chip, Collapse, Tooltip as MuiTooltip, Pagination } from '@mui/material'
import MusicLoader from '../../components/Common/MusicLoader'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { styled } from '@mui/material/styles'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid, AreaChart, Area } from 'recharts'
import spotifyLogo from '../../assets/img/spotify.png'
import appleMusicLogo from '../../assets/img/apple_music.png'
import amazonMusicLogo from '../../assets/img/amazon_music.png'
import pandoraLogo from '../../assets/img/pandora.png'
import deezerLogo from '../../assets/img/deezer.png'
import soundcloudLogo from '../../assets/img/soundcloud.png'
import youtubeLogo from '../../assets/img/youtube.png'
import youtubeShortsLogo from '../../assets/img/youtube_shorts.png'
import youtubeContentIdLogo from '../../assets/img/youtube_content_id.png'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Redirect } from 'react-router-dom'
import { apiGet } from '../../utils'
import * as actions from './actions'
import moment from 'moment'
import SEO from '../../components/SEO'

const CHART_COLORS = ['#1a73e8', '#00bfa5', '#667eea', '#764ba2', '#4facfe', '#f59e0b', '#ef4444', '#10b981']

const STORE_OPTIONS = [
    { value: 'amazon_music', label: 'Amazon Music', logo: amazonMusicLogo },
    { value: 'apple_music', label: 'Apple Music', logo: appleMusicLogo },
    { value: 'deezer', label: 'Deezer', logo: deezerLogo },
    { value: 'pandora', label: 'Pandora', logo: pandoraLogo },
    { value: 'soundcloud', label: 'SoundCloud', logo: soundcloudLogo },
    { value: 'spotify', label: 'Spotify', logo: spotifyLogo },
    { value: 'youtube', label: 'YouTube', logo: youtubeLogo },
    { value: 'youtube_content_id', label: 'YouTube Content ID', logo: youtubeContentIdLogo },
    { value: 'youtube_shorts', label: 'YouTube Shorts', logo: youtubeShortsLogo },
]

const PageContainer = styled(Box)({
    padding: '24px',
    minHeight: 'calc(100vh - 100px)',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
})

const MetricCard = styled(Box)(({ gradient }) => ({
    background: gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    padding: '24px',
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
    },
}))

const MetricValue = styled(Typography)({
    fontSize: '2.5rem',
    fontWeight: 700,
    marginTop: '8px',
    fontFamily: "'DM Sans', sans-serif",
})

const MetricLabel = styled(Typography)({
    fontSize: '0.875rem',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: "'DM Sans', sans-serif",
})

const ChartCard = styled(Box)({
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
})

const ChartTitle = styled(Typography)({
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
})

const StyledTableContainer = styled(TableContainer)({
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    backgroundColor: 'white',
    overflow: 'hidden',
    '& .MuiTable-root': {
        fontFamily: "'DM Sans', sans-serif",
        backgroundColor: 'white',
    },
    '& .MuiTableHead-root': {
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        '& .MuiTableCell-head': {
            color: 'white',
            fontWeight: 700,
            fontSize: '0.9375rem',
            fontFamily: "'DM Sans', sans-serif",
            padding: '16px',
        },
    },
    '& .MuiTableBody-root': {
        backgroundColor: 'white',
        '& .MuiTableRow-root': {
            backgroundColor: 'white !important',
            transition: 'background-color 0.2s ease',
            '&:nth-of-type(odd)': {
                backgroundColor: '#f9f9f9 !important',
            },
            '&:hover': {
                backgroundColor: '#f0f7ff !important',
            },
        },
    },
    '& .MuiTableCell-body': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.875rem',
        color: '#202124',
        padding: '16px',
        borderBottom: '1px solid #f0f0f0',
    },
})

const StyledTabs = styled(Tabs)({
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    minHeight: '40px',
    borderBottom: '2px solid rgba(26, 115, 232, 0.1)',
    '& .MuiTabs-indicator': {
        background: 'linear-gradient(90deg, #1a73e8 0%, #00bfa5 100%)',
        height: '2px',
        borderRadius: '2px 2px 0 0',
    },
    '& .MuiTabs-flexContainer': {
        justifyContent: 'flex-start',
    },
})

const StyledTab = styled(Tab)({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '40px',
    padding: '6px 16px',
    transition: 'all 0.3s ease',
    color: '#000',
    '&.Mui-selected': {
        background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.08) 0%, rgba(0, 191, 165, 0.08) 100%)',
        color: '#000',
    },
    '&:hover': {
        background: 'rgba(26, 115, 232, 0.05)',
    },
})

const tooltipStyle = {
    backgroundColor: '#202124',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    color: '#fff',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.8rem',
}

function groupBy(data, key) {
    const map = {}
    data.forEach((t) => {
        if (!map[t[key]]) {
            map[t[key]] = { name: t[key], tracks: 0, streams: 0 }
        }
        map[t[key]].tracks += 1
        map[t[key]].streams += t.streams
    })
    return Object.values(map).sort((a, b) => b.streams - a.streams)
}

function parseDDMMYYYY(dateStr) {
    const [dd, mm, yyyy] = dateStr.split('-')
    return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd))
}

function groupRangesByMonth(ranges) {
    const groups = {}
    ranges.forEach((range) => {
        const date = parseDDMMYYYY(range.startDate)
        const label = moment(date).format('MMMM YYYY')
        if (!groups[label]) {
            groups[label] = { label, sortDate: date, ranges: [] }
        }
        groups[label].ranges.push(range)
    })
    return Object.values(groups).sort((a, b) => b.sortDate - a.sortDate)
}

export default function StreamingStats() {
    const [activeTab, setActiveTab] = useState(0)
    const [tablePage, setTablePage] = useState(1)
    const [store, setStore] = useState('spotify')

    // Analytics access control
    const [analyticsAccess, setAnalyticsAccess] = useState(null)
    const [accessChecked, setAccessChecked] = useState(false)

    // API-driven state
    const [tracks, setTracks] = useState([])
    const [totalStreams, setTotalStreams] = useState(0)
    const [availableRanges, setAvailableRanges] = useState([])
    const [availableStores, setAvailableStores] = useState(null)
    const [loading, setLoading] = useState(true)

    // Multi-select state
    const [selectedRanges, setSelectedRanges] = useState([])
    const [pendingRanges, setPendingRanges] = useState([])
    const [multiFileData, setMultiFileData] = useState({})
    const [expandedMonths, setExpandedMonths] = useState({})
    const [multiLoading, setMultiLoading] = useState(false)

    // Date range picker state
    const [dateAnchorEl, setDateAnchorEl] = useState(null)
    const [selectedYear, setSelectedYear] = useState(null)

    const datePopoverOpen = Boolean(dateAnchorEl)
    const isMultiSelect = selectedRanges.length > 1
    const groupedRanges = useMemo(() => groupRangesByMonth(availableRanges), [availableRanges])

    const availableYears = useMemo(() => {
        const years = new Set()
        availableRanges.forEach((range) => {
            const date = parseDDMMYYYY(range.startDate)
            years.add(date.getFullYear())
        })
        return [...years].sort((a, b) => b - a)
    }, [availableRanges])

    const filteredGroupedRanges = useMemo(() => {
        if (!selectedYear) return groupedRanges
        return groupedRanges.filter((g) => g.sortDate.getFullYear() === selectedYear)
    }, [groupedRanges, selectedYear])

    const fetchData = useCallback((filename) => {
        setLoading(true)
        actions.getAnalyticsData(filename)
            .then((res) => {
                const data = Array.isArray(res.body) ? res.body : []
                setTracks(data)
                setTotalStreams(data.reduce((sum, t) => sum + t.streams, 0))
                setLoading(false)
            })
            .catch(() => {
                setTracks([])
                setTotalStreams(0)
                setLoading(false)
            })
    }, [])

    const fetchMultipleFiles = useCallback((ranges) => {
        setMultiLoading(true)
        setLoading(true)
        Promise.all(
            ranges.map((range) =>
                actions.getAnalyticsData(range.filename)
                    .then((res) => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: Array.isArray(res.body) ? res.body : [],
                    }))
                    .catch(() => ({
                        filename: range.filename,
                        label: range.label,
                        startDate: range.startDate,
                        data: [],
                    }))
            )
        ).then((results) => {
            const dataMap = {}
            results.forEach((r) => {
                dataMap[r.filename] = { label: r.label, startDate: r.startDate, data: r.data }
            })
            setMultiFileData(dataMap)
            setMultiLoading(false)
            setLoading(false)
        })
    }, [])

    const fetchDateRanges = useCallback((storeName) => {
        setLoading(true)
        setMultiFileData({})
        actions.getAvailableDateRanges(storeName)
            .then((res) => {
                const ranges = Array.isArray(res.body) ? res.body : []
                setAvailableRanges(ranges)
                if (ranges.length > 0) {
                    const mostRecent = [...ranges].sort((a, b) => parseDDMMYYYY(b.startDate) - parseDDMMYYYY(a.startDate))[0]
                    setSelectedRanges([mostRecent])
                    setPendingRanges([mostRecent])
                    fetchData(mostRecent.filename)
                    const grouped = groupRangesByMonth(ranges)
                    const expanded = {}
                    grouped.forEach((g) => { expanded[g.label] = true })
                    setExpandedMonths(expanded)
                    const years = new Set()
                    ranges.forEach((r) => {
                        const d = parseDDMMYYYY(r.startDate)
                        years.add(d.getFullYear())
                    })
                    const sortedYears = [...years].sort((a, b) => b - a)
                    if (sortedYears.length > 0) setSelectedYear(sortedYears[0])
                } else {
                    setSelectedRanges([])
                    setPendingRanges([])
                    setTracks([])
                    setTotalStreams(0)
                    setSelectedYear(null)
                    setLoading(false)
                }
            })
            .catch(() => {
                setAvailableRanges([])
                setSelectedRanges([])
                setTracks([])
                setTotalStreams(0)
                setSelectedYear(null)
                setLoading(false)
            })
    }, [])

    const fetchAvailableStores = useCallback(() => {
        actions.getAvailableDateRanges()
            .then((res) => {
                const allFiles = Array.isArray(res.body) ? res.body : []
                setAvailableStores(new Set(allFiles.map(f => f.store)))
            })
            .catch(() => setAvailableStores(new Set()))
    }, [])

    useEffect(() => {
        apiGet('/api/getUserDetails')
            .then((res) => {
                const access = res?.body?.analytics === 'true'
                setAnalyticsAccess(access)
                setAccessChecked(true)
                if (access) {
                    fetchDateRanges(store)
                    fetchAvailableStores()
                }
            })
            .catch(() => {
                setAnalyticsAccess(false)
                setAccessChecked(true)
            })
    }, [])

    useEffect(() => {
        if (analyticsAccess) {
            fetchDateRanges(store)
        }
    }, [store, fetchDateRanges, analyticsAccess])

    useEffect(() => {
        if (availableStores === null) return
        if (availableStores.has(store)) return
        const firstAvailable = STORE_OPTIONS.find(opt => availableStores.has(opt.value))
        if (firstAvailable) {
            setStore(firstAvailable.value)
        }
    }, [availableStores])

    const handleStoreChange = (e) => {
        setStore(e.target.value)
        setTablePage(1)
    }

    const handleDateChipClick = (event) => {
        setPendingRanges([...selectedRanges])
        setDateAnchorEl(event.currentTarget)
    }

    const handleDatePopoverClose = () => {
        setDateAnchorEl(null)
    }

    const handleRangeToggle = (range) => {
        setPendingRanges((prev) => {
            const exists = prev.some((r) => r.filename === range.filename)
            if (exists) {
                return prev.filter((r) => r.filename !== range.filename)
            }
            if (prev.length >= 5) return prev
            return [...prev, range]
        })
    }

    const toggleMonthExpanded = (monthLabel) => {
        setExpandedMonths((prev) => ({ ...prev, [monthLabel]: !prev[monthLabel] }))
    }

    const handleYearChange = (e) => {
        const year = e.target.value
        setSelectedYear(year)
        const newExpanded = { ...expandedMonths }
        groupedRanges
            .filter((g) => g.sortDate.getFullYear() === year)
            .forEach((g) => { newExpanded[g.label] = true })
        setExpandedMonths(newExpanded)
    }

    const handleApply = () => {
        if (pendingRanges.length === 0) return
        setSelectedRanges([...pendingRanges])
        if (pendingRanges.length === 1) {
            fetchData(pendingRanges[0].filename)
        } else {
            fetchMultipleFiles(pendingRanges)
        }
        setDateAnchorEl(null)
    }

    // Single-file chart data
    const { summaryCards, barData, barTitle, pieData, pieTitle, artistData, showArtistChart, tableTitle, tableColumns, tableRows } = useMemo(() => {
        if (tracks.length === 0) {
            return {
                summaryCards: [
                    { label: 'Total Tracks', value: '0' },
                    { label: 'Total Streams', value: '0' },
                    { label: 'Top Track', value: '-', small: true },
                    { label: 'Avg. Streams', value: '0' },
                ],
                barTitle: 'No data',
                barData: [],
                pieTitle: 'No data',
                pieData: [],
                artistData: [],
                showArtistChart: false,
                tableTitle: 'No data',
                tableColumns: [],
                tableRows: [],
            }
        }

        if (activeTab === 1) {
            const grouped = groupBy(tracks, 'artist')
            const top = grouped[0]
            return {
                summaryCards: [
                    { label: 'Total Artists', value: grouped.length },
                    { label: 'Total Streams', value: totalStreams.toLocaleString() },
                    { label: 'Top Artist', value: top.name, small: true },
                    { label: 'Avg. Streams/Artist', value: Math.round(totalStreams / grouped.length).toLocaleString() },
                ],
                barTitle: `Top ${Math.min(grouped.length, 15)} Artists by Streams`,
                barData: grouped.slice(0, 15).map((a) => ({
                    name: a.name.length > 20 ? a.name.substring(0, 20) + '...' : a.name,
                    streams: a.streams,
                })),
                pieTitle: 'Artist Stream Share',
                pieData: (() => {
                    const top5 = grouped.slice(0, 5)
                    const othersStreams = grouped.slice(5).reduce((sum, a) => sum + a.streams, 0)
                    const result = top5.map((a) => ({
                        name: a.name.length > 25 ? a.name.substring(0, 25) + '...' : a.name,
                        value: a.streams,
                    }))
                    if (othersStreams > 0) result.push({ name: 'Others', value: othersStreams })
                    return result
                })(),
                artistData: [],
                showArtistChart: false,
                tableTitle: 'Artist Details',
                tableColumns: ['No.', 'Artist', 'Tracks', 'Streams'],
                tableRows: grouped.map((a, i) => ({
                    index: i + 1,
                    cells: [a.name, a.tracks, a.streams.toLocaleString()],
                })),
            }
        }

        if (activeTab === 2) {
            const grouped = groupBy(tracks, 'label')
            const top = grouped[0]
            return {
                summaryCards: [
                    { label: 'Total Labels', value: grouped.length },
                    { label: 'Total Streams', value: totalStreams.toLocaleString() },
                    { label: 'Top Label', value: top.name, small: true },
                    { label: 'Avg. Streams/Label', value: Math.round(totalStreams / grouped.length).toLocaleString() },
                ],
                barTitle: `Top ${Math.min(grouped.length, 15)} Labels by Streams`,
                barData: grouped.slice(0, 15).map((l) => ({
                    name: l.name.length > 20 ? l.name.substring(0, 20) + '...' : l.name,
                    streams: l.streams,
                })),
                pieTitle: 'Label Stream Share',
                pieData: (() => {
                    const top5 = grouped.slice(0, 5)
                    const othersStreams = grouped.slice(5).reduce((sum, l) => sum + l.streams, 0)
                    const result = top5.map((l) => ({
                        name: l.name.length > 25 ? l.name.substring(0, 25) + '...' : l.name,
                        value: l.streams,
                    }))
                    if (othersStreams > 0) result.push({ name: 'Others', value: othersStreams })
                    return result
                })(),
                artistData: [],
                showArtistChart: false,
                tableTitle: 'Label Details',
                tableColumns: ['No.', 'Label', 'Tracks', 'Streams'],
                tableRows: grouped.map((l, i) => ({
                    index: i + 1,
                    cells: [l.name, l.tracks, l.streams.toLocaleString()],
                })),
            }
        }

        // Songs tab (default)
        const artistMap = {}
        tracks.forEach((t) => {
            artistMap[t.artist] = (artistMap[t.artist] || 0) + t.streams
        })
        const artistChartData = Object.entries(artistMap)
            .map(([name, streams]) => ({
                name: name.length > 30 ? name.substring(0, 30) + '...' : name,
                fullName: name,
                streams,
            }))
            .sort((a, b) => b.streams - a.streams)
            .slice(0, 10)

        return {
            summaryCards: [
                { label: 'Total Tracks', value: tracks.length },
                { label: 'Total Streams', value: totalStreams.toLocaleString() },
                { label: 'Top Track', value: tracks[0].title, small: true },
                { label: 'Avg. Streams', value: Math.round(totalStreams / tracks.length).toLocaleString() },
            ],
            barTitle: `Top ${Math.min(tracks.length, 15)} Tracks by Streams`,
            barData: tracks.slice(0, 15).map((t) => ({
                name: t.title.length > 20 ? t.title.substring(0, 20) + '...' : t.title,
                streams: t.streams,
            })),
            pieTitle: 'Stream Distribution',
            pieData: (() => {
                const top5 = tracks.slice(0, 5)
                const othersStreams = tracks.slice(5).reduce((sum, t) => sum + t.streams, 0)
                const result = top5.map((t) => ({
                    name: t.title.length > 25 ? t.title.substring(0, 25) + '...' : t.title,
                    value: t.streams,
                }))
                if (othersStreams > 0) result.push({ name: 'Others', value: othersStreams })
                return result
            })(),
            artistData: artistChartData,
            showArtistChart: true,
            tableTitle: 'Track Details',
            tableColumns: ['No.', 'Title', 'Artist', 'Label', 'Streams'],
            tableRows: tracks.map((t, i) => ({
                index: i + 1,
                cells: [t.title, t.artist, t.label, t.streams.toLocaleString()],
            })),
        }
    }, [activeTab, tracks, totalStreams])

    // Multi-file comparison data
    const { comparisonData, multiSummaryCards, avgStreamsPerPeriod } = useMemo(() => {
        if (!isMultiSelect || Object.keys(multiFileData).length === 0) {
            return { comparisonData: null, multiSummaryCards: [], avgStreamsPerPeriod: 0 }
        }

        const sortedRanges = selectedRanges
            .map((r) => ({
                ...r,
                parsedDate: parseDDMMYYYY(r.startDate),
                fileData: multiFileData[r.filename],
            }))
            .filter((r) => r.fileData)
            .sort((a, b) => a.parsedDate - b.parsedDate)

        if (sortedRanges.length === 0) {
            return { comparisonData: null, multiSummaryCards: [], avgStreamsPerPeriod: 0 }
        }

        // Overall: one entry per range with total streams
        const overallData = sortedRanges.map((r) => ({
            period: r.fileData.label,
            streams: r.fileData.data.reduce((sum, t) => sum + t.streams, 0),
        }))

        // Per-song: top 8 songs by appearance count, then total streams as tiebreaker
        const songCounts = {}
        const songStreams = {}
        sortedRanges.forEach((r) => {
            r.fileData.data.forEach((t) => {
                songCounts[t.title] = (songCounts[t.title] || 0) + 1
                songStreams[t.title] = (songStreams[t.title] || 0) + t.streams
            })
        })
        const topSongs = Object.keys(songCounts)
            .sort((a, b) => {
                if (songCounts[b] !== songCounts[a]) return songCounts[b] - songCounts[a]
                return songStreams[b] - songStreams[a]
            })
            .slice(0, 10)

        const perSongData = sortedRanges.map((r) => {
            const entry = { period: r.fileData.label }
            const trackMap = {}
            r.fileData.data.forEach((t) => {
                trackMap[t.title] = (trackMap[t.title] || 0) + t.streams
            })
            topSongs.forEach((song) => {
                entry[song] = trackMap[song] || 0
            })
            return entry
        })

        // Summary cards for multi-select
        const totalStreamsAll = overallData.reduce((sum, d) => sum + d.streams, 0)
        let topTrackOverall = '-'
        if (Object.keys(songStreams).length > 0) {
            topTrackOverall = Object.entries(songStreams).sort((a, b) => b[1] - a[1])[0][0]
        }
        const avgStreams = sortedRanges.length > 0 ? Math.round(totalStreamsAll / sortedRanges.length) : 0

        return {
            comparisonData: { overallData, perSongData, topSongs },
            multiSummaryCards: [
                { label: 'Periods Compared', value: sortedRanges.length },
                { label: 'Total Streams', value: totalStreamsAll.toLocaleString() },
                { label: 'Top Track Overall', value: topTrackOverall, small: true },
            ],
            avgStreamsPerPeriod: avgStreams,
        }
    }, [isMultiSelect, selectedRanges, multiFileData])

    const CARD_GRADIENTS = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #00bfa5 0%, #00897b 100%)',
        'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        'linear-gradient(135deg, #1a73e8 0%, #1557b0 100%)',
    ]

    if (!accessChecked) {
        return <MusicLoader />
    }

    if (!analyticsAccess) {
        return <Redirect to="/" />
    }

    if (loading && tracks.length === 0) {
        return <MusicLoader />
    }

    if (!loading && tracks.length === 0 && availableRanges.length === 0) {
        return (
            <PageContainer>
                <SEO title="Analytics" description="View your streaming analytics and insights" />
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                        Select Store
                    </Typography>
                    <FormControl size="small" sx={{
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '2px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 600,
                            '& fieldset': { borderColor: 'rgba(26, 115, 232, 0.2)' },
                            '&:hover fieldset': { borderColor: '#1a73e8' },
                            '&.Mui-focused fieldset': { borderColor: '#1a73e8' },
                        },
                    }}>
                        <Select
                            value={store}
                            onChange={handleStoreChange}
                            IconComponent={KeyboardArrowDownIcon}
                            renderValue={(value) => {
                                const opt = STORE_OPTIONS.find(o => o.value === value)
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {opt?.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                        {opt?.label || value}
                                    </Box>
                                )
                            }}
                            MenuProps={{
                                autoFocus: false,
                                PaperProps: {
                                    sx: {
                                        borderRadius: '2px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                        mt: 1,
                                        backgroundColor: '#fff',
                                        width: 'auto',
                                        minWidth: 'unset',
                                    },
                                },
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                sx: {
                                    '& .MuiList-root': { width: '100%' },
                                },
                            }}
                        >
                            {STORE_OPTIONS.map(opt => {
                                const isStoreDisabled = availableStores !== null && !availableStores.has(opt.value)
                                const menuItem = (
                                    <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={isStoreDisabled}
                                        sx={{
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: isStoreDisabled ? '#9aa0a6' : '#202124',
                                            '&.Mui-selected': { backgroundColor: 'rgba(26, 115, 232, 0.08)', color: '#202124' },
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                            '&.Mui-disabled': { opacity: 0.5 },
                                        }}
                                    >
                                        {opt.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain', filter: isStoreDisabled ? 'grayscale(100%)' : 'none' }} />}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                                            {opt.label}
                                            {isStoreDisabled && (
                                                <Typography component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#9aa0a6', ml: 1 }}>
                                                    No data
                                                </Typography>
                                            )}
                                        </Box>
                                    </MenuItem>
                                )
                                return isStoreDisabled ? (
                                    <MuiTooltip key={opt.value} title="No analytics available for this store" placement="right">
                                        <span>{menuItem}</span>
                                    </MuiTooltip>
                                ) : menuItem
                            })}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column', gap: 2 }}>
                    <CalendarTodayIcon sx={{ fontSize: 64, color: '#dadce0' }} />
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                        No analytics data available
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#9aa0a6', fontFamily: "'DM Sans', sans-serif" }}>
                        Analytics data will appear here once files are uploaded by the admin.
                    </Typography>
                </Box>
            </PageContainer>
        )
    }

    return (
        <PageContainer>
            <SEO title="Analytics" description="View your streaming analytics and insights" />
            {/* Analytics Disclaimer */}
            <Box sx={{
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRadius: '16px',
                padding: '16px 20px',
                mb: 3,
                border: '2px solid #ff9800',
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
            }}>
                <InfoOutlinedIcon sx={{ color: '#f57c00', fontSize: 24, mt: '2px', flexShrink: 0 }} />
                <Box>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: '#e65100', mb: 0.5 }}>
                        Analytics Data May Differ from Royalty Reports
                    </Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', fontWeight: 500, color: '#5d4037', lineHeight: 1.6 }}>
                        The stream counts shown in Analytics are intended to provide near real-time performance insights and may differ from the figures shown in your Royalty Reports. Many Digital Service Providers (DSPs), including Spotify, Apple Music, YouTube, and Deezer, include non-royalty-bearing streams in their analytics data. These streams help measure audience engagement but do not generate revenue and therefore do not appear in Royalty Reports.
                    </Typography>
                </Box>
            </Box>

            {/* Store Selector & Date Range */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                    Select Store
                </Typography>
                <FormControl size="small" sx={{
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '2px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        '& fieldset': { borderColor: 'rgba(26, 115, 232, 0.2)' },
                        '&:hover fieldset': { borderColor: '#1a73e8' },
                        '&.Mui-focused fieldset': { borderColor: '#1a73e8' },
                    },
                    '& .MuiSelect-select': {
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        color: '#202124',
                    },
                    '& .MuiSelect-icon': {
                        color: '#5f6368',
                    },
                }}>
                    <Select
                        value={store}
                        onChange={handleStoreChange}
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={(value) => {
                            const opt = STORE_OPTIONS.find(o => o.value === value)
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {opt?.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                    {opt?.label || value}
                                </Box>
                            )
                        }}
                        MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                                sx: {
                                    borderRadius: '2px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                    mt: 1,
                                    backgroundColor: '#fff',
                                    width: 'auto',
                                    minWidth: 'unset',
                                },
                            },
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            sx: {
                                '& .MuiList-root': { width: '100%' },
                            },
                        }}
                    >
                        {STORE_OPTIONS.map(opt => {
                            const isStoreDisabled = availableStores !== null && !availableStores.has(opt.value)
                            const menuItem = (
                                <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={isStoreDisabled}
                                    sx={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: isStoreDisabled ? '#9aa0a6' : '#202124',
                                        '&.Mui-selected': { backgroundColor: 'rgba(26, 115, 232, 0.08)', color: '#202124' },
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                        '&.Mui-disabled': { opacity: 0.5 },
                                    }}
                                >
                                    {opt.logo && <img src={opt.logo} alt={opt.label} style={{ width: 20, height: 20, objectFit: 'contain', filter: isStoreDisabled ? 'grayscale(100%)' : 'none' }} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                                        {opt.label}
                                        {isStoreDisabled && (
                                            <Typography component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#9aa0a6', ml: 1 }}>
                                                No data
                                            </Typography>
                                        )}
                                    </Box>
                                </MenuItem>
                            )
                            return isStoreDisabled ? (
                                <MuiTooltip key={opt.value} title="No analytics available for this store" placement="right">
                                    <span>{menuItem}</span>
                                </MuiTooltip>
                            ) : menuItem
                        })}
                    </Select>
                </FormControl>

                {/* Date Range Picker */}
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                    Select Date
                </Typography>
                {selectedRanges.length > 0 && (
                    <Box
                        onClick={handleDateChipClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '2px',
                            backgroundColor: '#fff',
                            color: '#202124',
                            border: '1px solid rgba(26, 115, 232, 0.2)',
                            cursor: 'pointer',
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            transition: 'all 0.2s ease',
                            userSelect: 'none',
                            '&:hover': {
                                borderColor: '#1a73e8',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            },
                        }}
                    >
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#1a73e8' }} />
                        {selectedRanges.length === 1 ? selectedRanges[0].label : `${selectedRanges.length} ranges selected`}
                        <KeyboardArrowDownIcon sx={{ fontSize: 18, ml: 0.5, color: '#5f6368' }} />
                    </Box>
                )}



                <Popover
                    open={datePopoverOpen}
                    anchorEl={dateAnchorEl}
                    onClose={handleDatePopoverClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: '2px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                mt: 1,
                                overflow: 'hidden',
                                minWidth: 520,
                                backgroundColor: '#fff',
                            },
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', minHeight: 280, backgroundColor: '#fff' }}>
                        {/* Left Panel - Grouped Ranges with Checkboxes */}
                        <Box sx={{ width: '55%', borderRight: '1px solid #e8eaed', py: 1, backgroundColor: '#fafafa', maxHeight: 380, overflowY: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'DM Sans', sans-serif" }}>
                                    Date Ranges
                                </Typography>
                                {availableYears.length > 1 ? (
                                    <FormControl size="small" sx={{ minWidth: 90 }}>
                                        <Select
                                            value={selectedYear || ''}
                                            onChange={handleYearChange}
                                            IconComponent={KeyboardArrowDownIcon}
                                            sx={{
                                                fontSize: '0.75rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                                                color: '#1a73e8',
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26, 115, 232, 0.2)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1a73e8' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1a73e8' },
                                                '& .MuiSelect-select': { py: 0.5, px: 1.5, pr: '28px !important' },
                                                '& .MuiSelect-icon': { color: '#1a73e8', fontSize: 20 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: '#fff',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                                        mt: 0.5,
                                                    },
                                                },
                                            }}
                                        >
                                            {availableYears.map((year) => (
                                                <MenuItem key={year} value={year} sx={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#202124' }}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : availableYears.length === 1 ? (
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a73e8', fontFamily: "'DM Sans', sans-serif" }}>
                                        {availableYears[0]}
                                    </Typography>
                                ) : null}
                            </Box>
                            {filteredGroupedRanges.map((group) => (
                                <Box key={group.label}>
                                    {/* Month Header */}
                                    <Box
                                        onClick={() => toggleMonthExpanded(group.label)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 2.5,
                                            py: 1,
                                            cursor: 'pointer',
                                            backgroundColor: '#f0f0f0',
                                            borderBottom: '1px solid #e8eaed',
                                            '&:hover': { backgroundColor: '#e8eaed' },
                                        }}
                                    >
                                        <Typography sx={{
                                            fontSize: '0.8125rem',
                                            fontWeight: 700,
                                            color: '#3c4043',
                                            fontFamily: "'DM Sans', sans-serif",
                                        }}>
                                            {moment(group.sortDate).format('MMMM')}
                                        </Typography>
                                        {expandedMonths[group.label] ? (
                                            <ExpandLessIcon sx={{ fontSize: 18, color: '#5f6368' }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 18, color: '#5f6368' }} />
                                        )}
                                    </Box>
                                    {/* Range Items */}
                                    <Collapse in={expandedMonths[group.label]}>
                                        {group.ranges.map((range) => {
                                            const isSelected = pendingRanges.some((r) => r.filename === range.filename)
                                            const isDisabled = !isSelected && pendingRanges.length >= 5
                                            return (
                                                <Box
                                                    key={range.filename}
                                                    onClick={() => !isDisabled && handleRangeToggle(range)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1,
                                                        py: 0.5,
                                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                        opacity: isDisabled ? 0.5 : 1,
                                                        backgroundColor: isSelected ? '#e8f0fe' : 'transparent',
                                                        borderLeft: isSelected ? '3px solid #1a73e8' : '3px solid transparent',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            backgroundColor: isDisabled ? 'transparent' : isSelected ? '#e8f0fe' : '#f1f3f4',
                                                        },
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        size="small"
                                                        sx={{
                                                            color: '#9aa0a6',
                                                            '&.Mui-checked': { color: '#1a73e8' },
                                                            p: 0.5,
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => !isDisabled && handleRangeToggle(range)}
                                                    />
                                                    <Typography sx={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: isSelected ? 600 : 400,
                                                        color: isSelected ? '#1a73e8' : '#3c4043',
                                                        fontFamily: "'DM Sans', sans-serif",
                                                        ml: 0.5,
                                                    }}>
                                                        {range.label}
                                                    </Typography>
                                                </Box>
                                            )
                                        })}
                                    </Collapse>
                                </Box>
                            ))}
                            {availableRanges.length === 0 && (
                                <Typography sx={{ px: 2.5, py: 2, color: '#9aa0a6', fontSize: '0.875rem', fontFamily: "'DM Sans', sans-serif" }}>
                                    No date ranges available for this store.
                                </Typography>
                            )}
                        </Box>

                        {/* Right Panel - Selection Summary */}
                        <Box sx={{ width: '45%', p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#fff' }}>
                            <CalendarTodayIcon sx={{ fontSize: 36, color: pendingRanges.length > 0 ? '#1a73e8' : '#dadce0', mb: 1.5 }} />
                            {pendingRanges.length > 0 ? (
                                <>
                                    <Typography sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: '#202124',
                                        fontFamily: "'DM Sans', sans-serif",
                                        textAlign: 'center',
                                        mb: 1,
                                    }}>
                                        {pendingRanges.length} range{pendingRanges.length !== 1 ? 's' : ''} selected
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        maxHeight: 220,
                                        overflowY: 'auto',
                                        width: '100%',
                                    }}>
                                        {pendingRanges.map((r) => (
                                            <Chip
                                                key={r.filename}
                                                label={r.label}
                                                size="small"
                                                onDelete={() => handleRangeToggle(r)}
                                                sx={{
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    backgroundColor: '#e8f0fe',
                                                    color: '#1a73e8',
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#1a73e8',
                                                        fontSize: 16,
                                                        '&:hover': { color: '#1557b0' },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Typography sx={{
                                    fontSize: '0.875rem',
                                    color: '#9aa0a6',
                                    fontFamily: "'DM Sans', sans-serif",
                                    textAlign: 'center',
                                }}>
                                    Select up to 5 date ranges
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: '#e8eaed' }} />

                    {/* Footer Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1.5, gap: 1, backgroundColor: '#fff' }}>
                        <Button
                            onClick={handleDatePopoverClose}
                            sx={{
                                color: '#5f6368',
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                textTransform: 'uppercase',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApply}
                            disabled={pendingRanges.length === 0}
                            sx={{
                                backgroundColor: '#1a73e8',
                                color: '#fff',
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                textTransform: 'uppercase',
                                borderRadius: '8px',
                                px: 3,
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#1557b0',
                                    boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)',
                                },
                            }}
                        >
                            Apply{pendingRanges.length > 0 ? ` (${pendingRanges.length})` : ''}
                        </Button>
                    </Box>
                </Popover>
            </Box>

            {loading ? (
                <MusicLoader />
            ) : isMultiSelect && comparisonData ? (
                <>
                    {/* Selected date ranges */}
                    {selectedRanges.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#5f6368', fontFamily: "'DM Sans', sans-serif", mr: 0.5 }}>
                                Showing results for:
                            </Typography>
                            {selectedRanges.map((r) => (
                                <Chip
                                    key={r.filename}
                                    label={r.label}
                                    size="small"
                                    sx={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        backgroundColor: '#e8f0fe',
                                        color: '#1a73e8',
                                        height: 22,
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Multi-Select Summary Cards + Streams Trend */}
                    <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                        {multiSummaryCards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
                                <MetricCard gradient={CARD_GRADIENTS[i]} sx={{ width: '100%' }}>
                                    <MetricLabel>{card.label}</MetricLabel>
                                    <MetricValue sx={card.small ? {
                                        fontSize: '1.5rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } : undefined}>{card.value}</MetricValue>
                                </MetricCard>
                            </Grid>
                        ))}
                        {comparisonData?.overallData && comparisonData.overallData.length > 0 && (() => {
                            const od = comparisonData.overallData
                            const peak = od.reduce((max, d) => d.streams > max.streams ? d : max, od[0])
                            const low = od.reduce((min, d) => d.streams < min.streams ? d : min, od[0])
                            const first = od[0].streams
                            const last = od[od.length - 1].streams
                            const pct = first > 0 ? Math.round(((last - first) / first) * 100) : 0
                            const isUp = pct >= 0
                            return (
                                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key="trend">
                                    <MetricCard gradient={CARD_GRADIENTS[3]} sx={{ width: '100%', justifyContent: 'flex-start', pb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                            <Box>
                                                <MetricLabel>Streams Trend</MetricLabel>
                                                <MetricValue sx={{ fontSize: '1.75rem', lineHeight: 1.2 }}>
                                                    {avgStreamsPerPeriod.toLocaleString()}
                                                </MetricValue>
                                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", mt: 0.25 }}>
                                                    avg / period
                                                </Typography>
                                            </Box>
                                            {od.length >= 2 && (
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                                    px: 1, py: 0.25, borderRadius: '12px',
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                }}>
                                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif" }}>
                                                        {isUp ? '\u25B2' : '\u25BC'} {isUp ? '+' : ''}{pct}%
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 50, mt: 0.5 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={od} margin={{ top: 5, right: 0, bottom: 0, left: 0 }}>
                                                    <defs>
                                                        <linearGradient id="streamsTrendGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="rgba(255,255,255,0.4)" stopOpacity={1} />
                                                            <stop offset="100%" stopColor="rgba(255,255,255,0)" stopOpacity={1} />
                                                        </linearGradient>
                                                    </defs>
                                                    <Tooltip
                                                        contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '0.7rem', fontFamily: "'DM Sans', sans-serif", color: '#fff' }}
                                                        formatter={(value) => [value.toLocaleString(), 'Streams']}
                                                        labelStyle={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="streams"
                                                        stroke="rgba(255,255,255,0.8)"
                                                        strokeWidth={2}
                                                        fill="url(#streamsTrendGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 3, fill: '#fff', strokeWidth: 0 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5, gap: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>
                                                Peak: {peak.streams.toLocaleString()}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>
                                                Low: {low.streams.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </MetricCard>
                                </Grid>
                            )
                        })()}
                    </Grid>

                    {/* Per-Song Streams Trend */}
                    {comparisonData.topSongs.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <ChartCard sx={{
                                    background: 'linear-gradient(135deg, #4facfe15 0%, #00f2fe20 50%, #43e97b10 100%)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': { content: '""', position: 'absolute', top: '-20%', right: '-5%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, #4facfe20 0%, transparent 70%)', zIndex: 0 },
                                    '&::after': { content: '""', position: 'absolute', bottom: '-25%', left: '-5%', width: '250px', height: '250px', borderRadius: '50%', background: 'radial-gradient(circle, #43e97b15 0%, transparent 70%)', zIndex: 0 },
                                }}>
                                    <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Per-Song Streams Trend (Top {comparisonData.topSongs.length})</ChartTitle>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <LineChart data={comparisonData.perSongData} margin={{ top: 5, right: 30, bottom: 40, left: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eaed" />
                                            <XAxis
                                                dataKey="period"
                                                tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#666' }}
                                                angle={-25}
                                                textAnchor="end"
                                                height={80}
                                            />
                                            <YAxis tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#666' }} />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #e8eaed',
                                                    borderRadius: '10px',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    padding: '12px 16px',
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: '0.8rem',
                                                    color: '#202124',
                                                }}
                                                itemStyle={{ padding: '2px 0', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: '#000', fontWeight: 700 }}
                                                labelStyle={{ fontWeight: 700, fontSize: '0.8rem', color: '#202124', marginBottom: 6, borderBottom: '1px solid #e8eaed', paddingBottom: 6 }}
                                                wrapperStyle={{ zIndex: 100 }}
                                            />
                                            <Legend wrapperStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem' }} />
                                            {comparisonData.topSongs.map((song, i) => (
                                                <Line
                                                    key={song}
                                                    type="monotone"
                                                    dataKey={song}
                                                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Period Comparison Table */}
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2, color: '#202124', fontFamily: "'DM Sans', sans-serif" }}>
                        Period Comparison
                    </Typography>
                    <StyledTableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Period</TableCell>
                                    <TableCell>Total Tracks</TableCell>
                                    <TableCell>Total Streams</TableCell>
                                    <TableCell>Top Track</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedRanges
                                    .map((r) => ({ ...r, parsedDate: parseDDMMYYYY(r.startDate) }))
                                    .sort((a, b) => a.parsedDate - b.parsedDate)
                                    .map((range) => {
                                        const fd = multiFileData[range.filename]
                                        const data = fd?.data || []
                                        const totalStr = data.reduce((sum, t) => sum + t.streams, 0)
                                        const sorted = [...data].sort((a, b) => b.streams - a.streams)
                                        const topTrack = sorted.length > 0 ? sorted[0].title : '-'
                                        return (
                                            <TableRow key={range.filename}>
                                                <TableCell sx={{ fontWeight: 600 }}>{fd?.label || range.label}</TableCell>
                                                <TableCell>{data.length}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{totalStr.toLocaleString()}</TableCell>
                                                <TableCell sx={{ color: '#5f6368' }}>{topTrack}</TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                </>
            ) : (
                <>
                    {/* Filter Tabs */}
                    <Box sx={{ mb: 3, backgroundColor: '#fff', borderRadius: '2px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', display: 'inline-block' }}>
                        <StyledTabs value={activeTab} onChange={(e, v) => { setActiveTab(v); setTablePage(1) }}>
                            <StyledTab label="Filter by Songs" />
                            <StyledTab label="Filter by Artists" />
                            <StyledTab label="Filter by Labels" />
                        </StyledTabs>
                    </Box>

                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                        {summaryCards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
                                <MetricCard gradient={CARD_GRADIENTS[i]} sx={{ width: '100%' }}>
                                    <MetricLabel>{card.label}</MetricLabel>
                                    <MetricValue sx={card.small ? {
                                        fontSize: '1.5rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } : undefined}>{card.value}</MetricValue>
                                </MetricCard>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Charts */}
                    {barData.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={7}>
                                <ChartCard sx={{
                                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba220 50%, #f093fb10 100%)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': { content: '""', position: 'absolute', top: '-20%', left: '-5%', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, #667eea20 0%, transparent 70%)', zIndex: 0 },
                                    '&::after': { content: '""', position: 'absolute', bottom: '-25%', right: '-5%', width: '260px', height: '260px', borderRadius: '50%', background: 'radial-gradient(circle, #f093fb15 0%, transparent 70%)', zIndex: 0 },
                                }}>
                                    <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>{barTitle}</ChartTitle>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
                                            <defs>
                                                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#1a73e8" stopOpacity={1} />
                                                    <stop offset="100%" stopColor="#00bfa5" stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#666' }}
                                                angle={-45}
                                                textAnchor="end"
                                                interval={0}
                                                height={80}
                                            />
                                            <YAxis tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#666' }} />
                                            <Tooltip contentStyle={tooltipStyle} cursor={false} />
                                            <Bar dataKey="streams" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ChartCard sx={{
                                    background: 'linear-gradient(135deg, #f093fb25 0%, #f5576c20 50%, #fa709a15 100%)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': { content: '""', position: 'absolute', top: '-20%', right: '-5%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, #f093fb30 0%, transparent 70%)', zIndex: 0 },
                                    '&::after': { content: '""', position: 'absolute', bottom: '-30%', left: '-5%', width: '250px', height: '250px', borderRadius: '50%', background: 'radial-gradient(circle, #fee14025 0%, transparent 70%)', zIndex: 0 },
                                }}>
                                    <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>{pieTitle}</ChartTitle>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="45%"
                                                outerRadius={95}
                                                innerRadius={55}
                                                dataKey="value"
                                                paddingAngle={4}
                                                stroke="#fff"
                                                strokeWidth={1.5}
                                                cornerRadius={8}
                                                strokeLinejoin="round"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#202124',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                    color: '#fff',
                                                    padding: '12px',
                                                }}
                                                itemStyle={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}
                                            />
                                            <Legend
                                                wrapperStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Top Performing Artists — only on Songs tab */}
                    {showArtistChart && artistData.length > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <ChartCard sx={{
                                    background: 'linear-gradient(135deg, #a18cd120 0%, #fbc2eb20 50%, #f6d36515 100%)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': { content: '""', position: 'absolute', top: '-20%', right: '-5%', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, #a18cd125 0%, transparent 70%)', zIndex: 0 },
                                    '&::after': { content: '""', position: 'absolute', bottom: '-25%', left: '-5%', width: '260px', height: '260px', borderRadius: '50%', background: 'radial-gradient(circle, #fbc2eb20 0%, transparent 70%)', zIndex: 0 },
                                }}>
                                    <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Top 10 Performing Artists</ChartTitle>
                                    <ResponsiveContainer width="100%" height={Math.max(350, artistData.length * 45)}>
                                        <BarChart data={artistData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 20 }}>
                                            <defs>
                                                <linearGradient id="artistBarGradient" x1="0" y1="0" x2="1" y2="0">
                                                    <stop offset="0%" stopColor="#667eea" stopOpacity={1} />
                                                    <stop offset="100%" stopColor="#764ba2" stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <YAxis
                                                dataKey="name"
                                                type="category"
                                                width={220}
                                                tick={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", fill: '#333' }}
                                                interval={0}
                                            />
                                            <XAxis
                                                type="number"
                                                tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#666' }}
                                            />
                                            <Tooltip
                                                contentStyle={tooltipStyle}
                                                cursor={false}
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload.length) {
                                                        const data = payload[0].payload
                                                        return (
                                                            <div style={{ ...tooltipStyle, padding: '10px 14px', maxWidth: 300, wordWrap: 'break-word' }}>
                                                                <p style={{ margin: 0, fontWeight: 600, marginBottom: 4 }}>{data.fullName}</p>
                                                                <p style={{ margin: 0, color: '#5f6368' }}>Streams: {data.streams.toLocaleString()}</p>
                                                            </div>
                                                        )
                                                    }
                                                    return null
                                                }}
                                            />
                                            <Bar dataKey="streams" fill="url(#artistBarGradient)" radius={[0, 4, 4, 0]} barSize={28} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </Grid>
                        </Grid>
                    )}

                    {/* Data Table */}
                    {tableColumns.length > 0 && (() => {
                        const rowsPerPage = 200
                        const totalPages = Math.ceil(tableRows.length / rowsPerPage)
                        const paginatedRows = tableRows.slice((tablePage - 1) * rowsPerPage, tablePage * rowsPerPage)
                        return (
                            <>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2, color: '#202124', fontFamily: "'DM Sans', sans-serif" }}>
                                    {tableTitle}
                                </Typography>
                                <StyledTableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {tableColumns.map((col) => (
                                                    <TableCell key={col}>{col}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedRows.map((row) => (
                                                <TableRow key={row.index}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.index}</TableCell>
                                                    {row.cells.map((cell, ci) => (
                                                        <TableCell
                                                            key={ci}
                                                            sx={
                                                                ci === 0
                                                                    ? { fontWeight: 600, fontSize: '0.9375rem', color: '#202124' }
                                                                    : ci === row.cells.length - 1
                                                                        ? { fontWeight: 600 }
                                                                        : { color: '#5f6368' }
                                                            }
                                                        >
                                                            {cell}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </StyledTableContainer>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px', gap: 2 }}>
                                    <Typography sx={{ fontSize: '0.875rem', color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                                        Showing {((tablePage - 1) * rowsPerPage) + 1}–{Math.min(tablePage * rowsPerPage, tableRows.length)} of {tableRows.length}
                                    </Typography>
                                    {totalPages > 1 && (
                                        <Pagination
                                            count={totalPages}
                                            page={tablePage}
                                            onChange={(e, page) => setTablePage(page)}
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontWeight: 600,
                                                    color: '#1a73e8',
                                                },
                                                '& .Mui-selected': {
                                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                    color: 'white',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </>
                        )
                    })()}
                </>
            )}
        </PageContainer>
    )
}
